import { Helmet } from 'react-helmet-async';
import { usePricing } from '@/context/PricingContext';

export default function StructuredData() {
  const pricing = usePricing();
  const llPrice = pricing.local_launch?.price || '399';

  const organization = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://uidesigns.co.uk/#organization",
    "name": "UI DESIGNS",
    "alternateName": "UI Designs Bournemouth",
    "description": `Affordable web design, managed hosting, and ongoing care for small businesses in Bournemouth, Poole, and Christchurch. Websites from £${llPrice}.`,
    "url": "https://uidesigns.co.uk",
    "telephone": "+447466981840",
    "email": "info@uidesigns.co.uk",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Bournemouth",
      "addressRegion": "Dorset",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "50.7192",
      "longitude": "-1.8808"
    },
    "priceRange": `£${llPrice} - £3,800+`,
    "openingHours": "Mo-Fr 09:00-18:00",
    "sameAs": [],
    "founder": {
      "@type": "Person",
      "name": "Danny Vincent"
    },
    "areaServed": [
      { "@type": "City", "name": "Bournemouth" },
      { "@type": "City", "name": "Poole" },
      { "@type": "City", "name": "Christchurch" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Web Design Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Local Launch Website",
          "price": llPrice,
          "priceCurrency": "GBP",
          "description": "Single-page website to get your business online fast."
        },
        {
          "@type": "Offer",
          "name": "Starter Website Package",
          "price": "800",
          "priceCurrency": "GBP",
          "description": "3-page website with mobile design, contact form, and basic SEO."
        },
        {
          "@type": "Offer",
          "name": "Growth Website Package",
          "price": "1800",
          "priceCurrency": "GBP",
          "description": "10-page site with CRO, lead automation, and GA4 analytics."
        },
        {
          "@type": "Offer",
          "name": "Bespoke Website Package",
          "price": "3800",
          "priceCurrency": "GBP",
          "description": "Full custom build with e-commerce, advanced tracking, and premium design."
        }
      ]
    }
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "UI DESIGNS",
    "url": "https://uidesigns.co.uk",
    "description": `Affordable web design and managed hosting for small businesses in Bournemouth, Poole, and Christchurch. From £${llPrice}.`,
    "publisher": { "@id": "https://uidesigns.co.uk/#organization" }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does a website cost in Bournemouth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Websites start from £${llPrice} for a simple single-page site. Most small business sites fall in the £800–£3,800 range depending on pages, features, and complexity. We always give you a clear quote before any work starts.`
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to build a website?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A straightforward 5-page site could be ready in just a few days, while a larger project with custom features might take a few weeks. We always give you a realistic timeline upfront."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer website hosting and maintenance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. We offer monthly care plans from £60/month that include hosting, security, backups, and access to our custom admin panel where you can update your own site in seconds."
        }
      },
      {
        "@type": "Question",
        "name": "What is the admin panel?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Every site we build comes with a custom admin panel — a simple dashboard where you can update text, swap images, and make changes to your website in real time. No need to email and wait days for a small tweak. It is included free with all care plans."
        }
      },
      {
        "@type": "Question",
        "name": "Will my website work on mobile?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Every website we build is fully responsive and tested across all devices — phones, tablets, and desktops. Over 60% of web traffic comes from mobile, so this is non-negotiable."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do you cover?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We are based in Bournemouth and serve businesses across Bournemouth, Poole, Christchurch, Boscombe, Winton, Westbourne, Southbourne, and the wider Dorset and BCP Council area. We also work with clients remotely across the UK."
        }
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(organization)}</script>
      <script type="application/ld+json">{JSON.stringify(website)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </Helmet>
  );
}
